<template>
  <div class="h-100 w-100">
    <!-- Header -->
    <div class="content-entry-container mt9v">
      <img class="mt5 mt0-l mw6-l" src="./assets/images/logo.png" />
    </div>
    <!-- Navigation -->
    <nav class="flex fixed justify-center top-0 w-100">
      <a @click="isShowingGallery = false" href="#">Home</a>
      <a @click="isShowingGallery = false" href="#learn">Learn</a>
      <a @click="isShowingGallery = false" href="#faq">F.A.Q.</a>
      <a @click="isShowingGallery = false" href="#follow">Follow</a>
      <a @click="isShowingGallery = true" href="#errata" style="color: #D9E745;">Cards & Errata</a>
    </nav>
    <!-- Kickstarter -->
    <div class="mb4 pt0-l w-100">
      <a
        class="black flex flex-column items-center link w-100"
        href="https://www.amazon.com/gp/product/B09DQWY4HT"
        target="_blank"
        ><h2 class="mt0-l">Order Now On</h2>
        <img class="mw5-l w-60" src="./assets/images/Amazon logo.png" />
      </a>
    </div>
    <!-- Introduction -->
    <div v-if="!isShowingGallery" class="bg-paragon-orange content-entry-container">
      <img
        class="img-drop-shadow mb5 mw6-l"
        src="./assets/images/card-spread.png"
      />
      <p class="f4-l mw8 tc">
        <i>Paragon: Trials of the Chosen</i> is a strategic, expandable card
        game (ECG) that features no luck, a compelling approach to turn
        sequence, complete control of your starting hand and draw order, and an
        innovative combat system that isn't reliant upon minions. Build your
        deck using eight disciplines from four schools: Martial, Magic, Mythos,
        and Mischief. Choose your Avatars and battle to be the last Paragon!
      </p>
    </div>
    <div v-if="!isShowingGallery" class="content-entry-container">
      <img class="mb5 mw6-l" src="./assets/images/table-spread.png" />
      <p class="f4-l mw8 tc">
        Paragon seeks to capture all of the excitement and strategy of
        traditional card games without the frustrating randomness or pay-to-win
        requirements. You will never roll a die, flip a coin, or draw an unknown
        card. Rather, in Paragon you choose the order of your 20-card deck.
        This, combined with the game's turn sequencing, rewards methodical and
        forward-thinking play.
        <br />
        <br />
        Even mirror matches have a large degree of variability in Paragon,
        because we are including mechanics that we refer to as "pseudo-random."
        These are card effects that can change the order of a player’s deck, for
        example. This allows for games to feel fresh while still injecting a
        level of uncertainty to keep players on their toes.
      </p>
    </div>
    <!-- Videos -->
    <div v-if="!isShowingGallery" class="bg-paragon-blue content-entry-container">
      <p class="f4-l mw8 tc">
        As an expandable card game, Paragon includes all cards in a single box,
        and all players have access to the same card pool. You can purchase one
        box and have access to hundreds of card combinations without any
        additional purchase. All future expansions and releases will stick to
        this expandable, single-purchase distribution strategy.
      </p>
    </div>
    <!-- How to Play -->
    <div v-if="!isShowingGallery" id="learn" class="content-entry-container">
      <h2>How to Play</h2>
      <agile ref="h2pCarousel" class="mw8-l w-80 tc">
        <div class="slide">
          <div>
            <h3>Step 1: Choose Your Avatars</h3>
            <p class="mb4 mt4">
              You can choose any two, but beginners may wish to choose Avatars
              that share at least one discipline (border color). An Avatar's
              <b>disciplines tell you which cards you may attach</b> to it
              during the game.
            </p>
            <img
              class="img-drop-shadow mw6-l"
              src="./assets/images/h2p-avatars.png"
            />
          </div>
        </div>
        <div class="slide">
          <div>
            <h3>Step 2: Add Equipment and Abilities</h3>
            <p class="mb4 mt4">
              Choose exactly 20 Equipment and/or Ability cards to add to your
              deck. They <b>must share a discipline</b> with at least one of
              your Avatars. Beginners are encouraged to choose cards with a
              range of energy costs (top left corner of the card).
            </p>
            <img
              class="img-drop-shadow mw6-l"
              src="./assets/images/h2p-equip-abilities.png"
            />
          </div>
        </div>
        <div class="slide">
          <div>
            <h3>Step 3: Sort Your Deck</h3>
            <p class="mb4 mt4">
              In <i>Paragon</i>, you <b>never shuffle your deck</b>. Instead,
              before the game starts, choose the order of all 20 cards. New
              players may wish to sort their deck by energy cost instead,
              putting low cost cards first.
            </p>
            <img class="mw6-l" src="./assets/images/h2p-deck.png" />
          </div>
        </div>
        <div class="slide">
          <div v-if="h2pCarousel && h2pCarousel.getCurrentSlide() == 3">
            <h3>Step 4: Enter the Arena</h3>
            <p class="mb4 mt4">
              A typical <i>Paragon</i> board may look something like this during
              the game:
            </p>
            <a href="./assets/images/h2p-board.png" target="_blank"
              ><img class="mw6-l" src="./assets/images/h2p-board.png"
            /></a>
          </div>
        </div>
        <div class="slide">
          <div v-if="h2pCarousel && h2pCarousel.getCurrentSlide() == 4">
            <h3>Step 5: Begin the Trial</h3>
            <p class="mb4 mt4">
              The player with the most combined Avatar HP gains initiative,
              which means they take the first action. If Avatar HP is tied, use
              <i>Rock, Paper, Scissors</i> to decide.
            </p>
            <p>
              Players alternate taking one action on their turns. Possible
              actions include:
            </p>
            <div class="flex flex-row">
              <span class="w-25-l"></span>
              <ul class="ml-40 tl">
                <li>Play a card</li>
                <li>Declare an attack</li>
                <li>Use a card effect</li>
                <li>Exhaust an Avatar to gain 1 energy</li>
                <li>Pass</li>
              </ul>
              <span class="w-25-l"></span>
            </div>
            <p>
              After both players pass <b>in sequence</b>, the round ends.
              Players start a new round by incrementing the round number,
              setting their energy equal to the round number, readying all their
              cards, then drawing a card from their respective decks.
            </p>
          </div>
        </div>
        <div class="slide">
          <div v-if="h2pCarousel && h2pCarousel.getCurrentSlide() == 5">
            <h3>Ready for More?</h3>
            <p class="mb4 mt4">
              To learn the rules in-depth, feel free to download the
              <a href="./assets/paragon-rulebook.pdf" target="_blank"
                >rulebook (PDF)</a
              >.
            </p>
            <img class="mw6-l" src="./assets/images/h2p-rulebook.png" />
          </div>
        </div>
        <template slot="prevButton" class="fas fa"
          ><i class="fas fa-chevron-left"></i
        ></template>
        <template slot="nextButton"
          ><i class="fas fa-chevron-right"></i
        ></template>
      </agile>
    </div>
    <!-- Recommended Starter Decks -->
    <div v-if="!isShowingGallery" id="decks" class="content-entry-container flex-column items-center tl">
      <h2>Recommended Decks</h2>
      <div class="flex flex-row flex-wrap w-100">
        <div class="flex flex-column deck-col">
          <h3 class="tc">Deck 1 (Chivalry/Divinity)</h3>
          <p><strong>General Strategy:</strong> In game one, focus on using Shield Bash to generate extra damage and ensure you use Thane's ability to slow your opponent. If you cannot generate enough damage early, the Infernal Claymore should give you the means to close out the game. In game two, you might consider swapping strategies to utilize the combination of Blessing of Seto and Strength of Faith and place those cards in your opening hand.</p>
          <ul>
            <li><strong>Sir Aren, Oathkeeper</strong></li>
            <li><strong>Thane, War Cleric</strong></li>
            <li>Bolster</li>
            <li>Shield Bash</li>
            <li>Sword of Retribution</li>
            <li>Intimidation</li>
            <li>Meteor-Forged Tower Shield</li>
            <li>Galvanized Chainmail</li>
            <li>Weapon Mastery</li>
            <li>Heavensguard Longsword</li>
            <li>Disarm</li>
            <li>Ring of Royalty</li>
            <li>Spear of the Honorable</li>
            <li>Infernal Claymore</li>
            <li>Meteor-Forged Breastplate</li>
            <li>Mend</li>
            <li>Banish</li>
            <li>Strength of Faith</li>
            <li>Blessing of Seto</li>
            <li>Paragon's Wrath</li>
            <li>Armor of Hopesong</li>
            <li>Ranger's Longbow</li>
          </ul>
        </div>
        <div class="flex flex-column deck-col">
          <h3 class="tc">Deck 2 (Chronomancy/Shadow)</h3>
          <p><strong>General Strategy:</strong> In game one, focus on Oryon’s ability to give you two actions to take advantage of cards that come into play ready. Try to use Medallion of the Crescent Moon early, and then utilize repeated Astral Blades while triggering the Medallion’s ability to return to play. In game two, you might consider swapping Chaos Field and Infinium Orb into your hand to create a different combo that your opponent may not be prepared for. Consider putting Devolve in your opening hand or near the top of your deck if the opponent's ongoing abilities are a problem.</p>
          <ul>
            <li><strong>Time Sage Oryon</strong></li>
            <li><strong>Elan'co, Last of Her Kind</strong></li>
            <li>Everbark Wand</li>
            <li>Vengeful Blade</li>
            <li>Astral Blades</li>
            <li>Shadow Strike</li>
            <li>Rewind</li>
            <li>Exploit Weakness</li>
            <li>Medallion of the Crescent Moon</li>
            <li>Cloak of Sudden Darkness</li>
            <li>Venomsting Knife</li>
            <li>Time Stone Amulet</li>
            <li>Bloody Knuckles</li>
            <li>Dispatch</li>
            <li>Infinium Orb</li>
            <li>Bow of the Time-Lost Tribe</li>
            <li>Sage's Crystal</li>
            <li>Devolve</li>
            <li>Timely Ward</li>
            <li>Borrowed Time</li>
            <li>Paradox</li>
            <li>Chaos Field</li>
          </ul>
        </div>
      </div>
    </div>
    <!-- Frequently Asked Questions -->
    <div v-if="!isShowingGallery" id="faq" class="content-entry-container flex-column tl">
      <div class="flex-column align-left content-left justify-left faq-sizing">
        <h2>Frequently Asked Questions</h2>
        <div class="mt3 pointer" @click="selectedQuestion = selectedQuestion === 0 ? null : 0">
          <i v-if="selectedQuestion===0" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Q: What does it mean to "exhaust" a card?</b>
          <div v-if="selectedQuestion===0" class="pa2 pl4 mt1">
            A: To exhaust a card (denoted by the <img class="pl1 self-center" src="./assets/images/exhaust.png" /> symbol), rotate it 45 degrees clockwise. This means the card is now exhausted and cannot attack or use effects that require it to exhaust. Exhausted cards are readied at the beginning of each round.
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedQuestion = selectedQuestion === 1 ? null : 1">
          <i v-if="selectedQuestion===1" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Q: What does it mean to "detach" a card?</b>
          <div v-if="selectedQuestion===1" class="pa2 pl4 mt1">
            A: To detach an Equipment or Ability that an Avatar has equipped, simply send it to the owner's discard pile. Only cards that are already attached to an Avatar may be detached.
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedQuestion = selectedQuestion === 2 ? null : 2">
          <i v-if="selectedQuestion===2" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Q: The "Pyrewax Idol" card has no subtype listed. What is its subtype?</b>
          <div v-if="selectedQuestion===2" class="pa2 pl4 mt1">
            A: "Pyrewax Idol" is an Accessory card.
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedQuestion = selectedQuestion === 3 ? null : 3">
          <i v-if="selectedQuestion===3" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Q: Since cards come into play exhausted, will my "Strength of Faith" card trigger every time I attach a card to that Avatar?</b>
          <div v-if="selectedQuestion===3" class="pa2 pl4 mt1">
            A: No. Cards that come into play are already considered exhausted, so they will not trigger the effect of "Strength of Faith."
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedQuestion = selectedQuestion === 4 ? null : 4">
          <i v-if="selectedQuestion===4" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Q: How does the "corrosive" keyword work?</b>
          <div v-if="selectedQuestion===4" class="pa2 pl4 mt1">
            A: When a card with the "corrosive" keyword deals damage to an Equipment card, destroy that Equipment card.
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedQuestion = selectedQuestion === 5 ? null : 5">
          <i v-if="selectedQuestion===5" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Q: How does the "Coalescence" card work?</b>
          <div v-if="selectedQuestion===5" class="pa2 pl4 mt1">
            A: At the end of the round in which you play "Coalescence," you may carry over your remaining energy to the next round, adding it to your new energy pool. If "Coalescence" is played and then discarded before the end of the round, you will not gain its effect, because it is an Ongoing card.
          </div>
        </div>
      </div>
    </div>
    <!-- Follow -->
    <div v-if="!isShowingGallery"
      id="follow"
      class="bg-paragon-blue content-entry-container flex flex-column items-center tc"
    >
      <h2 class="normal">Follow <i>Paragon</i></h2>
      <div class="flex flex-row">
        <!-- Kickstarter -->
        <div>
          <a
            href="https://www.kickstarter.com/projects/midwintermages/paragon-trials-of-the-chosen-0"
            target="_blank"
            ><img
              class="ma3"
              src="./assets/images/kickstarter-logo-k-white.png"
            />
          </a>
        </div>
        <!-- Facebook -->
        <div>
          <a href="https://www.facebook.com/paragonecg/" target="_blank"
            ><i class="f1 fab fa-facebook ma3 white"></i
          ></a>
        </div>
        <!-- Twitter -->
        <div>
          <a href="https://twitter.com/midwintermages" target="_blank"
            ><i class="f1 fab fa-twitter ma3 white"></i
          ></a>
        </div>
        <!-- YouTube -->
        <div>
          <a
            href="https://www.youtube.com/channel/UCLSfNjbz6OoP427rtccmyeA"
            target="_blank"
            ><i class="f1 fab fa-youtube ma3 white"></i
          ></a>
        </div>
      </div>
      <div v-if="isShowingMailForm" class="flex flex-row">
        <mailchimp-subscribe
          url="https://gmail.us4.list-manage.com/subscribe/post-json"
          user-id="9e107cf996af2c0d3b209e6ee"
          list-id="365bbfe63a"
          @error="onError"
          @success="onSuccess"
        >
          <template v-slot="{ subscribe, setEmail, error, success, loading }">
            <form
              @submit.prevent="subscribe"
              class="flex flex-column items-center"
            >
              <div class="mb2 mt2 tc"><em>Email Newsletter</em></div>
              <div>
                <input
                  type="email"
                  placeholder="your@email.address"
                  @input="setEmail($event.target.value)"
                  class="mb2 mr2"
                />
                <button type="submit">Submit</button>
              </div>
              <div v-if="error">{{ errorMsg }}</div>
              <div v-if="success">Subscribed!</div>
              <div v-if="loading">Loading…</div>
            </form>
          </template>
        </mailchimp-subscribe>
      </div>
    </div>
    <!-- Errata -->
    <div v-if="isShowingGallery" id="errata" class="content-entry-container">
      <h2>Errata</h2>
      <p class="f4-l mw8 tc">The text on these cards has been updated to clarify their effects and fix certain rules issues. These changes are considered official, and should be used during competitive games of <i>Paragon</i>, even if your physical copy does not match.</p>
      <p class="f4-l mt3 mw8 tc">Download the PDF version 
        <a href="./assets/paragon-errata.pdf" target="_blank">here</a>.
      </p>
      <div class="align-center flex flex-column mt3 tl">
        <div class="mt3 pointer" @click="selectedErrata = selectedErrata === 0 ? null : 0">
          <i v-if="selectedErrata===0" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Bloodwater Rites</b>
          <div v-if="selectedErrata===0" class="flex flex-column flex-row-ns align-top pa3 mt1 mw8">
            <img
              class="mr3 mw5-l mw4 pa2 pointer"
              src="./assets/images/cards/cards1_front143.png"
              @click="zoomedImage = 'cards/cards1_front143.png'"
            />
            <p class="pa2"><b>Version 1:</b> "All of your Equipment with Power now has Attack instead."</p>
            <p class="pa2"><b>Current Version:</b> "All of your Avatars' Equipment with Power now has Attack instead."</p>
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedErrata = selectedErrata === 1 ? null : 1">
          <i v-if="selectedErrata===1" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Infinium Orb</b>
          <div v-if="selectedErrata===1" class="flex flex-column flex-row-ns align-top pa3 mt1 mw8">
            <img
              class="mr3 mw5-l mw4 pa2 pointer"
              src="./assets/images/cards/cards1_front16.png"
              @click="zoomedImage = 'cards/cards1_front16.png'"
            />
            <p class="pa2"><b>Version 1:</b> "Exhaust: Ready target attached Brutality or Chronomancy card."</p>
            <p class="pa2"><b>Current Version:</b> "Exhaust: Choose another attached Brutality or Chronomancy card and ready it."</p>
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedErrata = selectedErrata === 2 ? null : 2">
          <i v-if="selectedErrata===2" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Coalescence</b>
          <div v-if="selectedErrata===2" class="flex flex-column flex-row-ns align-top pa3 mt1 mw8">
            <img
              class="mr3 mw5-l mw4 pa2 pointer"
              src="./assets/images/cards/cards1_front171.png"
              @click="zoomedImage = 'cards/cards1_front171.png'"
            />
            <p class="pa2"><b>Version 1:</b> "At the end of the round, keep any Energy in your pool and destroy this card."</p>
            <p class="pa2"><b>Current Version:</b> "At the start of the round, gain Energy instead of setting it, then destroy this card."</p>
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedErrata = selectedErrata === 3 ? null : 3">
          <i v-if="selectedErrata===3" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Sir Aren, Sworn to Service</b>
          <div v-if="selectedErrata===3" class="flex flex-column flex-row-ns align-top pa3 mt1 mw8">
            <img
              class="mr3 mw5-l mw4 pa2 pointer"
              src="./assets/images/cards/cards2_back5.png"
              @click="zoomedImage = 'cards/cards2_back5.png'"
            />
            <p class="pa2"><b>Version 1:</b> "Once per game: This card becomes a 5/5 Fallen Avatar Two-Handed Weapon. Attach it to target Avatar, readied. When this card detaches, exile it."</p>
            <p class="pa2"><b>Current Version:</b> "Once per game: This card becomes a Two-Handed Weapon with 5 HP and 5 Attack. Attach it to an Avatar, readied. When this card detaches, exile it."</p>
          </div>
        </div>
        <div class="mt3 pointer" @click="selectedErrata = selectedErrata === 4 ? null : 4">
          <i v-if="selectedErrata===4" class="fas fa-chevron-down mr2 pointer"></i>
          <i v-else class="fas fa-chevron-right mr2 pointer"></i>
          <b>Thane, Eternal Martyr</b>
          <div v-if="selectedErrata===4" class="flex flex-column flex-row-ns align-top pa3 mt1 mw8">
            <img
              class="mr3 mw5-l mw4 pa2 pointer"
              src="./assets/images/cards/cards2_back7.png"
              @click="zoomedImage = 'cards/cards2_back7.png'"
            />
            <p class="pa2"><b>Version 1:</b> "Once per game: Exhaust target Avatar's cards. Deal damage equal to number of cards exhausted this way."</p>
            <p class="pa2"><b>Current Version:</b> "Once per game: Exhaust all cards attached to target Avatar. Deal damage equal to the number of cards exhausted in this way."</p>
          </div>
        </div>
      </div>
    </div>
    <!-- Card Gallery -->
    <div v-if="isShowingGallery" id="gallery" class="bg-paragon-orange content-entry-container">
      <h2>Card Gallery</h2>
      <agile
        class="mt3 mw8-l tc w-100"
        :autoplay="true"
        :dots="false"
        :navButtons="false"
        :slidesToShow="3"
      >
        <div v-for="card in cards" :key="card.id" class="slide">
          <img
            class="mw5-l mw4 pa3 pointer"
            :src="require('./assets/images/cards/' + card.name)"
            @click="zoomedImage = 'cards/' + card.name"
          />
        </div>
      </agile>
    </div>
    <!-- Tagline -->
    <div id="tagline" class="content-entry-container">
      <p>Make your own luck.</p>
    </div>
    <!-- Footer -->
    <footer class="w-100">
      © 2019-2020
      <a class="white" href="https://www.midwintermages.com">Midwinter Mages</a
      >. All rights reserved.
    </footer>
    <!-- Lightbox -->
    <div
      id="zoomed-image-container"
      v-if="zoomedImage"
      @click="zoomedImage = null"
      class="fixed h-100 left-0 top-0 w-100"
    >
      <img
        id="zoomed-image"
        :src="require('./assets/images/' + zoomedImage)"
        class="fixed"
      />
    </div>
  </div>
</template>

<script>
import MailchimpSubscribe from "vue-mailchimp-subscribe";
import { VueAgile } from "vue-agile";

export default {
  name: "App",
  components: {
    agile: VueAgile,
    mailchimpSubscribe: MailchimpSubscribe
  },

  data() {
    return {
      cards: [],
      error: false,
      errorMsg: "",
      h2pCarousel: null,
      isShowingGallery: false,
      isShowingMailForm: true,
      loading: false,
      selectedErrata: null,
      selectedQuestion: null,
      success: false,
      zoomedImage: null
    };
  },

  created() {
    this.cards = require("./assets/data/cards.json").cards;
  },

  mounted() {
    /* keep track of carousel so we can check its current slide index;
    allows us to hide big images that would otherwise disrupt the page flow */
    this.h2pCarousel = this.$refs.h2pCarousel;
  },

  methods: {
    onError(err) {
      if (err.includes("already subscribed")) {
        this.errorMsg =
          "That email address is already subscribed to this list.";
      } else {
        this.errorMsg = err;
      }
    },

    onSuccess() {
      //
    }
  }
};
</script>

<style>
* {
  box-sizing: border-box;
}

body {
  font-family: "Poppins", sans-serif;
  font-size: 1.08rem;
  height: 100%;
  margin: 0;
  width: 100%;
}

footer {
  background-color: #124b72;
  color: white;
  font-size: 1.5vmin;
  padding: 3vmin;
  text-align: center;
}

li {
  line-height: 200%;
}

nav {
  background-color: #404e66;
  z-index: 200;
}

nav a {
  background-color: #404e66;
  color: white;
  padding: 3vmin;
  text-align: center;
  text-decoration: none;
  width: 25vmin;
}

nav a:hover {
  background-color: #b28e41;
}

p {
  line-height: 200%;
  margin: 0;
  padding: 0;
}

.agile__actions {
  margin-top: 20px;
}

.agile__nav-button {
  background: transparent;
  border: none;
  color: #ccc;
  cursor: pointer;
  font-size: 24px;
  -webkit-transition-duration: 0.3s;
  transition-duration: 0.3s;
}

.agile__nav-button:hover {
  color: #888;
}

.agile__dot {
  margin: 0 10px;
}

.agile__dot button {
  background-color: #eee;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: block;
  height: 10px;
  font-size: 0;
  line-height: 0;
  margin: 0;
  padding: 0;
  -webkit-transition-duration: 0.3s;
  transition-duration: 0.3s;
  width: 10px;
}

.agile__dot--current button,
.agile__dot:hover button {
  background-color: #888;
}

.bg-paragon-blue {
  background-color: #4b6a89;
  color: #f5c300;
}

.bg-paragon-orange {
  background-color: #d9a438;
}

.content-entry-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 9vmin;
  padding-left: 5vmin;
  padding-right: 5vmin;
  padding-top: 9vmin;
  width: 100%;
}

.deck-col {
  padding-left: 25px;
  padding-right: 25px;
  width: 50%;
}

.faq-sizing {
  width: 50%;
}

.img-drop-shadow {
  filter: drop-shadow(0.5vmin 0.5vmin 0.5vmin #222);
}

.mt9v {
  margin-top: 9vmin;
}

#tagline {
  font-size: 3vmin;
  padding-bottom: 3vmin;
  padding-top: 3vmin;
  text-align: center;
}

#zoomed-image-container {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 201;
}

#zoomed-image {
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

@media only screen and (max-width: 600px) {
  .deck-col {
    width: 100%;
  }

  .faq-sizing {
    width: 100%;
  }
}
</style>
